import { _ } from "../../../polyglot/polyglot.js";
import { difference } from "../../../helpers/set-util.js";
import {
	eyeSlashIcon,
	plusIcon,
	trashAltIcon,
} from "../../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import KeyFigureSelectionWidget from "../KeyFigureSelectionWidget/KeyFigureSelectionWidget.js";
import ListWidget from "../../list/ListWidget/ListWidget.js";

/**
 * Renders a list of key figures. Each key figure can be removed from
 * the list thanks to a remove button next to each. Below the list, a
 * button allows to remove all key figures from the list and another
 * one allows for adding key figures to the list.
 *
 * @param {function} updateCallback - This function is called each
 * time the list of key figures is changed. An array of key figures is
 * passed as argument.
 */
export default class KeyFigureListEditor extends Widget {
	constructor({ updateCallback } = {}) {
		super(...arguments);

		this._updateCallback = updateCallback;

		this._myKeyFigures = new Set();
		this._allKeyFigures = new Set();

		this._listWidget = new ListWidget({
			itemRenderer: this._keyFigureRenderer.bind(this),
			emptyContent: this._renderNoKeyFiguresWarningOn.bind(this),
		});
	}

	setup({ myKeyFigures, allKeyFigures }) {
		this._myKeyFigures = new Set(myKeyFigures);
		this._allKeyFigures = new Set([...allKeyFigures, ...myKeyFigures]);

		this._listWidget.setItems(myKeyFigures);
	}

	renderContentOn(html) {
		html.div(this._listWidget, this._renderActionsOn.bind(this));
	}

	_renderNoKeyFiguresWarningOn(html) {
		html.label(
			{ klass: "hint-danger" },
			_("No KPIs selected. This is usually not a good idea..."),
		);
	}

	_renderActionsOn(html) {
		html.div(
			{ class: "btn-group pull-right", role: "group" },
			this._renderClearKeyFiguresButtonOn.bind(this),
			this._renderAddKeyFiguresButtonOn.bind(this),
		);
	}

	_renderClearKeyFiguresButtonOn(html) {
		html.button(
			{
				"data-test": "clear",
				class: "btn btn-default icon-and-text",
				click: () => this._removeAllKeyFigures(),
			},
			trashAltIcon({ class: "fa-large" }),
			_("Remove all KPIs"),
		);
	}

	_renderAddKeyFiguresButtonOn(html) {
		const enabled = this._canAddKeyFigures();

		html.button(
			{
				"data-test": "add",
				class: [
					"btn",
					"btn-default",
					"icon-and-text",
					{ disabled: !enabled },
				],
				click: () => this._showKeyFiguresToAddDialog(),
			},
			plusIcon({ class: "fa-large" }),
			_("Add KPIs from list"),
		);
	}

	_keyFigureRenderer(keyFigure) {
		return [
			this._keyFigureLabelRenderer(keyFigure),
			this._keyFigureActionsRenderer(keyFigure),
		];
	}

	_keyFigureLabelRenderer(keyFigure) {
		return keyFigure.isVisible()
			? this._keyFigureNameRenderer(keyFigure)
			: this._invisibleKeyFigureNameRenderer(keyFigure);
	}

	_invisibleKeyFigureNameRenderer(keyFigure) {
		return (html) =>
			html.span(
				{ class: "icon-and-text" },
				eyeSlashIcon({
					title: "This preset is not visible",
				}),
				html.del(this._keyFigureNameRenderer(keyFigure)),
			);
	}

	_keyFigureNameRenderer(keyFigure) {
		return keyFigure.getName();
	}

	_keyFigureActionsRenderer(keyFigure) {
		return (html) =>
			html.div(
				{ class: "actions pull-right" },
				html.button(
					{
						class: "btn btn-danger btn-xs",
						"data-test": "remove-kpi",
						click: () => this._removeKeyFigure(keyFigure),
					},
					_("Remove"),
				),
			);
	}

	_showKeyFiguresToAddDialog() {
		const keyFigurePicker = new KeyFigureSelectionWidget({
			multipleSelection: true,
			keyFigures: this._keyFiguresThatCanBeAdded(),
		});

		this._confirm({
			title: _("Select KPIs to add to the preset"),
			body: keyFigurePicker,
			okLabel: _("Add KPIs"),
			contentCssClass: "resizable-modal-content",
			extraButtonsRenderer: () => [
				(html) =>
					html.a(
						{
							class: "btn btn-link",
							"data-test": "select-all",
							click: () => keyFigurePicker.selectAll(),
						},
						_("Select all"),
					),
			],
			callback: () =>
				this._addKeyFigures(keyFigurePicker.getSelectedItems()),
		});
	}

	_canAddKeyFigures() {
		return this._keyFiguresThatCanBeAdded().length > 0;
	}

	_keyFiguresThatCanBeAdded() {
		return [...difference(this._allKeyFigures, this._myKeyFigures)];
	}

	_addKeyFigures(keyFiguresToAdd) {
		keyFiguresToAdd.forEach((keyFigureToAdd) =>
			this._myKeyFigures.add(keyFigureToAdd),
		);

		this._onMyKeyFiguresChanged();
	}

	_removeKeyFigure(keyFigureToRemove) {
		this._myKeyFigures.delete(keyFigureToRemove);
		this._onMyKeyFiguresChanged();
	}

	_removeAllKeyFigures() {
		this._myKeyFigures.clear();
		this._onMyKeyFiguresChanged();
	}

	_onMyKeyFiguresChanged() {
		const keyFigures = [...this._myKeyFigures];

		this._listWidget.setItems(keyFigures);
		this._updateCallback(keyFigures);
	}
}
